import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AdminService} from '../services/admin.service';
import {HTTP_STATUS_CODES} from '../shared.constants/all-constants';
import {
  EMPTY_TEXT,
  END_OF_DAY_SUFFIX,
  FIRST_ITEM_INDEX,
  FLEXIBLE_REPORT_TAB,
  INITIAL_COUNTER_VALUE,
  LEGACY_STANDARD_TAB_VALUE,
  MODAL_RESULT_OK,
  SECOND_SHIFT,
  START_OF_DAY_SUFFIX,
  THIRD_SHIFT
} from './report-datepicker.constants';
import {ReportDatepickerDateCoordinator} from './report-datepicker-date.coordinator';
import {
  ReportDatepickerShiftView,
  ReportDatepickerShiftWorkflowView,
  ShiftParams,
  ShiftResponse,
  ShiftSchedule,
  StatusError
} from './report-datepicker.types';
import {ReportDatepickerUiGateway} from './report-datepicker-ui.gateway';
import {TIMEOUTS} from './const-timeout';

@Injectable()
export class ReportDatepickerShiftCoordinator {
  constructor(
    private adminService: AdminService,
    private dates: ReportDatepickerDateCoordinator,
    private ui: ReportDatepickerUiGateway
  ) {
  }

  loadShift(view: ReportDatepickerShiftWorkflowView, userId: string): void {
    this.resetShiftState(view);
    this.adminService.getUsersShift(userId).subscribe(
      (response: Object) => {
        this.processResponse(view, response as ShiftResponse);
        this.dates.initializeReportDates(view);
      },
      () => undefined
    );
  }

  selectShift(view: ReportDatepickerShiftView, shift: number, scheduleId: string): void {
    view.shift = shift;
    view.scheduleId = scheduleId;
    const plannedSelected = view.planned === scheduleId;
    view.checkValue = plannedSelected;
    view.currenValue = !plannedSelected;
    view.flagDeleteBtn = plannedSelected;
  }

  changeTab(view: ReportDatepickerShiftView, tab: string): void {
    view.currentFlexbleId = null;
    view.currentTab = tab;
    view.from = view.dateCurrent;
    view.currentFlexbleId = tab == LEGACY_STANDARD_TAB_VALUE
      ? null : view.group.flexibleWorkSchedule[FIRST_ITEM_INDEX].id;
  }

  selectFlexibleSchedule(view: ReportDatepickerShiftView, scheduleId: string): void {
    view.currentFlexbleId = scheduleId;
  }

  syncManualSelection(view: ReportDatepickerShiftView): void {
    view.currenValue = view.checkValue;
  }

  deletePlannedShift(view: ReportDatepickerShiftWorkflowView): void {
    this.adminService.deleteEvent(view.plannedId).subscribe(
      () => this.loadShift(view, view.userId),
      () => undefined
    );
  }

  isSubmissionDisabled(view: ReportDatepickerShiftWorkflowView): boolean {
    return view.shift !== INITIAL_COUNTER_VALUE && view.checkValue &&
      (!this.dates.isFromAfterToday(view) || !this.dates.isRangeValid(view) ||
        this.dates.isCurrentShiftRange(view, view.shift, view.stateShift));
  }

  handleRequest(view: ReportDatepickerShiftView, from: string, to: string): void {
    const params = this.createShiftParameters(view, from, to);
    this.adminService.pullShift(params).subscribe(
      (response: HttpResponse<Object>) => this.processSubmission(response),
      (error: StatusError) => this.processSubmissionError(view, error)
    );
  }

  private resetShiftState(view: ReportDatepickerShiftView): void {
    view.scheduleId1 = EMPTY_TEXT;
    view.scheduleId = EMPTY_TEXT;
    view.scheduleId2 = EMPTY_TEXT;
    view.scheduleId3 = EMPTY_TEXT;
    view.planned = EMPTY_TEXT;
    view.plannedId = EMPTY_TEXT;
    view.currentShift = EMPTY_TEXT;
    view.flagDeleteBtn = false;
  }

  private processResponse(view: ReportDatepickerShiftView, body: ShiftResponse): void {
    if (body.current == null) {
      view.checkValue = false;
      view.currenValue = true;
      view.current = true;
      view.shift = INITIAL_COUNTER_VALUE;
      this.initializeDefaultSchedule(view, body);
    } else {
      this.handleCurrentSchedule(view, body);
    }
    this.applyPlannedSchedule(view, body.planned);
  }

  private initializeDefaultSchedule(
    view: ReportDatepickerShiftView, body: ShiftResponse
  ): void {
    this.applyGroupScheduleIds(view, true);
    view.shiftCurrent = body.planned;
    if (body.planned) {
      view.planned = body.planned.schedule.id;
      view.plannedId = body.planned.id;
      this.resolvePlannedShift(view);
    } else {
      view.currentShift = INITIAL_COUNTER_VALUE;
    }
  }

  private handleCurrentSchedule(view: ReportDatepickerShiftView, body: ShiftResponse): void {
    view.checkValue = false;
    view.currenValue = true;
    view.current = false;
    view.shiftCurrent = body.current;
    this.applyGroupScheduleIds(view, false);
    if (body.current.from) {
      view.disableFlag = false;
    }
    this.resolveCurrentShift(view);
    if (view.shiftCurrent.from) {
      view.checkValue = true;
      view.currenValue = false;
    }
  }

  private applyGroupScheduleIds(view: ReportDatepickerShiftView, selectFirst: boolean): void {
    if (!view.group) {
      return;
    }
    if (view.group.workSchedule) {
      view.scheduleId1 = view.group.workSchedule.id;
      if (selectFirst) {
        view.scheduleId = view.group.workSchedule.id;
        view.flagDeleteBtn = true;
      } else {
        view.scheduleId = view.group.workSchedule.id;
      }
    }
    this.applyAdditionalScheduleIds(view);
  }

  private applyAdditionalScheduleIds(view: ReportDatepickerShiftView): void {
    if (view.group.workScheduleId2) {
      view.scheduleId2 = view.group.workScheduleId2.id;
    }
    if (view.group.workScheduleId3) {
      view.scheduleId3 = view.group.workScheduleId3.id;
    }
  }

  private applyPlannedSchedule(
    view: ReportDatepickerShiftView, planned: ShiftSchedule
  ): void {
    if (planned) {
      view.shiftPlanned = planned;
      view.planned = planned.schedule.id;
      view.plannedId = planned.id;
    } else {
      view.planned = EMPTY_TEXT;
    }
  }

  private resolvePlannedShift(view: ReportDatepickerShiftView): void {
    if (view.shiftCurrent.schedule.id === view.scheduleId1) {
      this.setResolvedShift(view, INITIAL_COUNTER_VALUE);
    }
    if (view.shiftCurrent.schedule.id === view.scheduleId2) {
      this.setResolvedShift(view, SECOND_SHIFT);
    }
    if (view.shiftCurrent.schedule.id === view.scheduleId3) {
      this.setResolvedShift(view, THIRD_SHIFT);
    }
  }

  private resolveCurrentShift(view: ReportDatepickerShiftView): void {
    if (view.shiftCurrent.schedule.id === view.scheduleId1) {
      this.setCurrentShift(view, INITIAL_COUNTER_VALUE);
    }
    if (view.shiftCurrent.schedule.id === view.scheduleId2) {
      this.setCurrentShift(view, SECOND_SHIFT);
    }
    if (view.shiftCurrent.schedule.id === view.scheduleId3) {
      this.setCurrentShift(view, THIRD_SHIFT);
    }
  }

  private setResolvedShift(view: ReportDatepickerShiftView, shift: number): void {
    view.stateShift = shift;
    view.scheduleId = view.shiftCurrent.schedule.id;
  }

  private setCurrentShift(view: ReportDatepickerShiftView, shift: number): void {
    this.setResolvedShift(view, shift);
    view.shift = shift;
  }

  private createShiftParameters(
    view: ReportDatepickerShiftView, from: string, to: string
  ): ShiftParams {
    if (view.currenValue) {
      return view.currentTab == FLEXIBLE_REPORT_TAB
        ? {idUser: view.userId, scheduleId: view.currentFlexbleId,
          from: from + START_OF_DAY_SUFFIX}
        : {idUser: view.userId, scheduleId: view.scheduleId};
    }
    return {
      idUser: view.userId, scheduleId: view.scheduleId,
      from: from + START_OF_DAY_SUFFIX, to: to + END_OF_DAY_SUFFIX
    };
  }

  private processSubmission(response: HttpResponse<Object>): void {
    if (response.status === HTTP_STATUS_CODES.OK) {
      this.ui.close(MODAL_RESULT_OK);
    }
  }

  private processSubmissionError(view: ReportDatepickerShiftView, error: StatusError): void {
    if (error.status == HTTP_STATUS_CODES.CONFLICT) {
      view.shiftError = true;
      this.ui.schedule(() => view.shiftError = false, TIMEOUTS.MEDIUM_LONG);
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/modals/report-datepicker-shift.coordinator.ts